<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { t } from "@/plugins/i18n";
import { useApiClient } from "@/api/client";
import { ApiError, Result } from "@/api/base";
import { loadingWrapperLocal } from "@/api/helpers/loadingWrapper";
import { LaravelPaginatedCollection } from "@/types/collections";
import FilterList from "./FilterList.vue";
import PolarityToggle from "./PolarityToggle.vue";
import {
	isSingleChoiceSegment,
	segmentOptionsFor,
	takesCohortMonths,
	toFilterValue,
	toSegmentOption,
	type SegmentOption,
	type SegmentSource,
} from "./segmentOptions";
import {
	keysAfterDeselectAll,
	keysAfterOnly,
	keysAfterSelectAll,
	type ValueRow,
} from "./valueRow";
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;

const {
	dimension,
	apiClient,
	currentSegmentId = null,
} = defineProps<{
	dimension: DocumentDimensionDTO;
	apiClient: "explorer" | "segments" | "dashboard";
	currentSegmentId?: number | null;
}>();

const values = defineModel<FilterValueDTO[]>("values", { required: true });
const included = defineModel<boolean>("included", { required: true });
const cohortMonths = defineModel<number | null>("cohortMonths", {
	required: true,
});

const DEFAULT_COHORT_MONTHS = 12;
const COHORT_MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);

const isLoading = ref(false);
const segments = ref<SegmentOption[]>([]);

const nameClean = computed(() => dimension.name_clean ?? null);
const isSingleChoice = computed(() => isSingleChoiceSegment(nameClean.value));
const showsCohortMonths = computed(() => takesCohortMonths(nameClean.value));

const options = computed(() =>
	segmentOptionsFor(nameClean.value, segments.value, { currentSegmentId })
);

const rows = computed<ValueRow[]>(() =>
	options.value.map((option) => ({
		key: String(option.id),
		label: option.label ?? "",
	}))
);

const selectedKeys = computed(
	() => new Set(values.value.map((value) => String(value.id)))
);

const isChecked = (row: ValueRow) => selectedKeys.value.has(row.key);

const optionOf = (row: ValueRow) =>
	options.value.find((option) => String(option.id) === row.key);

const pick = (row: ValueRow) => {
	const option = optionOf(row);

	return option ? [toFilterValue(option)] : [];
};

const applyKeys = (next: Set<string>) => {
	values.value = options.value
		.filter((option) => next.has(String(option.id)))
		.map(toFilterValue);
};

const toggle = (row: ValueRow) => {
	if (isSingleChoice.value) {
		values.value = isChecked(row) ? [] : pick(row);
		return;
	}

	const next = new Set(selectedKeys.value);

	if (next.has(row.key)) {
		next.delete(row.key);
	} else {
		next.add(row.key);
	}

	applyKeys(next);
};

const only = (row: ValueRow, scope: ValueRow[]) =>
	applyKeys(keysAfterOnly(selectedKeys.value, scope, row));

const selectAll = (rows: ValueRow[]) =>
	applyKeys(keysAfterSelectAll(selectedKeys.value, rows));

const deselectAll = (rows: ValueRow[]) =>
	applyKeys(keysAfterDeselectAll(selectedKeys.value, rows));

const load = async () => {
	const request: Promise<
		Result<LaravelPaginatedCollection<SegmentSource>, ApiError>
	> = useApiClient(apiClient).indexSegments({
		page: 1,
		itemsPerPage: 100,
		sortBy: [{ key: "title", order: "asc" }],
	});

	const response = await loadingWrapperLocal(request, isLoading);

	if (!response.isOk()) {
		return;
	}

	segments.value = response.value.data.map(toSegmentOption);
};

watch(
	showsCohortMonths,
	(shows) => {
		cohortMonths.value = shows
			? cohortMonths.value ?? DEFAULT_COHORT_MONTHS
			: null;
	},
	{ immediate: true }
);

onMounted(load);
</script>
<template>
	<div class="d-flex flex-column ga-5">
		<section v-if="showsCohortMonths" class="zone">
			<h6 class="zone-label">{{ t("filterDialog.segment.cohortHeading") }}</h6>
			<p class="text-body-2 muted mb-0">
				{{ t("filterDialog.segment.cohortHint") }}
			</p>
			<v-select
				v-model="cohortMonths"
				:items="COHORT_MONTHS"
				hide-details
				density="compact"
				class="cohort"
			/>
		</section>
		<section class="zone">
			<header class="zone-header">
				<h6 class="zone-label">
					{{
						isSingleChoice
							? t("filterDialog.segment.headingSingle")
							: t("filterDialog.segment.heading")
					}}
				</h6>
				<polarity-toggle v-if="!isSingleChoice" v-model="included" />
			</header>
			<v-progress-linear v-if="isLoading" indeterminate height="2" />
			<filter-list
				:rows="rows"
				:is-checked="isChecked"
				:allow-bulk="!isSingleChoice"
				:allow-only="!isSingleChoice"
				:search-placeholder="t('filterDialog.segment.search')"
				:empty-message="t('filterDialog.segment.none')"
				@toggle="toggle"
				@only="only"
				@select-all="selectAll"
				@deselect-all="deselectAll"
			/>
			<p class="text-body-2 muted mb-0">
				{{ t("filterDialog.values.selectedCount", { count: values.length }) }}
			</p>
		</section>
	</div>
</template>
<style scoped lang="scss">
.zone {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.zone-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
}

.zone-label {
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: rgb(var(--color-on-background));
	margin-bottom: 0;
}

.muted {
	color: rgb(var(--color-on-background));
}

.cohort {
	width: fit-content;
	min-width: 8rem;
}
</style>
